import { useEffect, useState, useCallback } from 'react'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faList } from '@fortawesome/pro-solid-svg-icons/faList'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { rolesService } from 'api/configuration/roles'
import { IRole } from 'interfaces/roles'
import { PathRoutes } from 'enum/routes'
import { AvatarWithInfo } from 'components/reusables/avatarWithInfo/AvatarWithInfo'
import { RankmiTableWrapper } from './styled'
import AccessToCourses from './accessToCourses'
import AssignedUsers from './assignedUsers'
import Options from './options'
import Remove from './remove'

const Roles = () => {
  const { t } = useTranslation()
  const prefix = 'configuration.rolesAndPermissions.roles'
  const history = useHistory()
  const [roles, setRoles] = useState<IRole[]>([])
  const [loading, setLoading] = useState(false)
  const [searchCriteria, setSearchCriteria] = useState('')
  const [order, setOrder] = useState('ascend')
  const [fieldToOrder, setFieldToOrder] = useState('name')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedRole, setSelectedRole] = useState<IRole | null>(null)

  const getRoles = useCallback(
    async (
      page: number,
      criteria: string,
      sortOrder: string,
      sortField: string,
      perPage: number
    ) => {
      const orderBy = sortOrder === 'descend' ? 'desc' : 'asc'
      setLoading(true)
      try {
        const response = await rolesService.getRoles({
          page,
          perPage,
          criteria,
          order: orderBy,
          fieldToOrder: sortField,
        })
        setRoles(response.data.roles)
        setTotalRecords(response.data.total)
      } catch {
        setRoles([])
        setTotalRecords(0)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const refreshFirstPage = useCallback(() => {
    setCurrentPage(1)
    setSearchCriteria('')
    getRoles(1, '', order, fieldToOrder, rowsPerPage)
  }, [fieldToOrder, getRoles, order, rowsPerPage])

  useEffect(() => {
    getRoles(currentPage, searchCriteria, order, fieldToOrder, rowsPerPage)
  }, [])

  const onSearch = (value: string) => {
    setSearchCriteria(value)
    setCurrentPage(1)
    getRoles(1, value, order, fieldToOrder, rowsPerPage)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
    getRoles(value, searchCriteria, order, fieldToOrder, rowsPerPage)
  }

  const onSortChange = (value: { order: string; field: string }) => {
    setOrder(value.order)
    setFieldToOrder(value.field)
    setCurrentPage(1)
    getRoles(1, searchCriteria, value.order, value.field, rowsPerPage)
  }

  const onRowsPerPageChange = (value: number) => {
    setCurrentPage(1)
    setRowsPerPage(value)
    getRoles(1, searchCriteria, order, fieldToOrder, value)
  }

  const create = () => {
    history.push(PathRoutes.ROLES_AND_PERMISSIONS_ROLES_CREATE)
  }

  const confirmDeletion = (role: IRole) => {
    setSelectedRole(role)
    setShowDeleteModal(true)
  }

  const locale = {
    header: {
      addButton: t(`${prefix}.newRol`),
      rowsCount: t(`${prefix}.table.rowsCount`),
      rowsCount_plural: t(`${prefix}.table.rowsCount_plural`),
    },
  }

  const columns = [
    {
      title: `${t(`${prefix}.table.headers.name`)}`,
      key: 'name',
      sorteable: true,
      render: (name: string, role: IRole) => (
        <AvatarWithInfo
          title={name}
          subtitle={`#${role.code}`}
          fontWeight="normal"
          principalColor="initial"
          size={30}
          borderRadius={8}
        />
      ),
    },
    {
      title: `${t(`${prefix}.table.headers.description`)}`,
      key: 'description',
      sorteable: true,
      render: (description: string) => (description ? description : '-'),
    },
    {
      title: `${t(`${prefix}.table.headers.users`)}`,
      key: 'users',
      sorteable: true,
      width: 160,
      render: (_users: number, role: IRole) => <AssignedUsers role={role} />,
    },
    {
      title: `${t(`${prefix}.table.headers.accessToCourses.title`)}`,
      key: 'courses',
      sorteable: false,
      width: 171,
      render: (_courses: number, role: IRole) => (
        <AccessToCourses role={role} />
      ),
    },
    {
      width: 66,
      hideable: false,
      align: 'center',
      fixed: 'right',
      render: (role: IRole) => (
        <Options role={role} confirmDeletion={confirmDeletion} />
      ),
    },
  ]

  return (
    <RankmiTableWrapper>
      <RankmiTable
        columnHeaders={columns}
        addButtonIcon={faPlus}
        onAddClick={create}
        data={roles}
        countIcon={faList}
        search={searchCriteria}
        onSearch={onSearch}
        total={totalRecords}
        locale={locale}
        loading={loading}
        onSortChange={onSortChange}
        onPaginationChange={onPaginationChange}
        currentPage={currentPage}
        initialRowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        delaySearch
        responsive
      />
      {showDeleteModal && selectedRole && (
        <Remove
          role={selectedRole}
          removeFromView={refreshFirstPage}
          close={() => setShowDeleteModal(false)}
        />
      )}
    </RankmiTableWrapper>
  )
}

export default Roles
